import React, { useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Plus,
  Loader2,
  Trash2,
  Edit,
  Tag,
} from 'lucide-react';
import { toast } from 'sonner';

const ClientCategoriesTab = ({ api, clientCategories, fetchData }) => {
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategory, setNewCategory] = useState({ name: '', sort_order: 0 });
  const [creating, setCreating] = useState(false);

  const handleSaveCategory = async () => {
    setCreating(true);
    try {
      if (editingCategory) {
        await api().patch(`/admin/client-categories/${editingCategory.id}`, newCategory);
        toast.success('Category updated');
      } else {
        await api().post('/admin/client-categories', newCategory);
        toast.success('Category created');
      }
      setShowCategoryDialog(false);
      setEditingCategory(null);
      setNewCategory({ name: '', sort_order: 0 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save category');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    try {
      await api().delete(`/admin/client-categories/${categoryId}`);
      toast.success('Category deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-lg font-semibold text-zinc-900">Client Categories</h2>
          <p className="text-sm text-zinc-500">Manage categories for client classification</p>
        </div>
        <Button onClick={() => {
          setEditingCategory(null);
          setNewCategory({ name: '', sort_order: clientCategories.length + 1 });
          setShowCategoryDialog(true);
        }} data-testid="add-category-btn">
          <Plus className="mr-2 h-4 w-4" />
          Add Category
        </Button>
      </div>
      
      <Card>
        <CardContent className="p-0">
          <div className="divide-y divide-zinc-100">
            {clientCategories.map((category) => (
              <div key={category.id} className="flex items-center justify-between p-4 hover:bg-zinc-50">
                <div className="flex items-center gap-3">
                  <Tag className="h-5 w-5 text-zinc-400" />
                  <div>
                    <p className="font-medium text-zinc-900">{category.name}</p>
                    <p className="text-sm text-zinc-500">Sort order: {category.sort_order}</p>
                  </div>
                  {!category.active && (
                    <Badge className="bg-zinc-100 text-zinc-600">Inactive</Badge>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEditingCategory(category);
                      setNewCategory({ name: category.name, sort_order: category.sort_order });
                      setShowCategoryDialog(true);
                    }}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-500 hover:text-red-700"
                    onClick={() => handleDeleteCategory(category.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
            {clientCategories.length === 0 && (
              <div className="p-8 text-center text-zinc-500">
                <Tag className="h-12 w-12 mx-auto mb-3 text-zinc-300" />
                <p>No categories yet</p>
                <p className="text-sm">Add categories to classify clients</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Category Dialog */}
      <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5" />
              {editingCategory ? 'Edit Category' : 'New Category'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="category-name">Category Name *</Label>
              <Input
                id="category-name"
                value={newCategory.name}
                onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                placeholder="e.g., Commercial, NDIS"
                className="mt-2"
                data-testid="category-name-input"
              />
            </div>
            <div>
              <Label htmlFor="category-sort">Sort Order</Label>
              <Input
                id="category-sort"
                type="number"
                value={newCategory.sort_order}
                onChange={(e) => setNewCategory({ ...newCategory, sort_order: parseInt(e.target.value) || 0 })}
                className="mt-2"
                data-testid="category-sort-input"
              />
              <p className="text-xs text-zinc-500 mt-1">Lower numbers appear first</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCategoryDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveCategory} disabled={creating || !newCategory.name.trim()} data-testid="save-category-btn">
              {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editingCategory ? 'Save Changes' : 'Create Category'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientCategoriesTab;
